import React, { useMemo, useState } from 'react';

import { getBankName } from '@revenuemarket/bank';
import { SyncLine } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import { Button, Tooltip, Text, Colors, Flex } from '@revenuemarket/vds';
import { uniq } from 'es-toolkit';
import { SCRAPE_STATUS } from '~apis/scrape';
import useGetIntegratedScrapeStatus from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import useScrapeFloatingPanelStore from '~store/useScrapeFloatingPanelStore';

import CommonModal from '../Modal/CommonModal';
import ScrapeFloatingPanelHeader from './ScrapeFloatingPanelHeader';
import ScrapeFloatingPanelList from './ScrapeFloatingPanelList';
import { FloatingPanelContainer, MinimizedButton, MinimizedWrapper, SpinnerIcon } from './style';
import SwipeText from './SwipeText';
import { SCRAPE_STATUS_ITEM_TYPE, ScrapeStatusItem } from './types';

const ScrapeFloatingPanel: React.FC = () => {
  const { companyId } = useCompanyId();
  const [isMinimized, setIsMinimized] = useState(false);
  const { isOpen, closePanel } = useScrapeFloatingPanelStore();
  const { data: scrapeStatusData } = useGetIntegratedScrapeStatus(companyId ?? '');

  const scrapeStatus = scrapeStatusData?.Data?.data;
  const {
    bankAccountScrapingStatus = [],
    cardScrapingStatus = [],
    taxInvoiceScrapingStatus = [],
    cashReceiptScrapingStatus = [],
    overallStatus = 'IN_PROGRESS',
  } = scrapeStatus ?? {};

  const { organizationCount, ScrapeStatusItem } = useMemo(() => {
    const createStatusItem = (label: string, completedCount: number, totalCount: number) => {
      const isAllCompleted = completedCount === totalCount && totalCount > 0;
      const isNotConnected = totalCount === 0;

      const getStatus = () => {
        if (isNotConnected) return 'notConnected';
        if (isAllCompleted) return hasError(overallStatus) ? 'error' : 'success';
        return 'inProgress';
      };

      return {
        label,
        completedCount,
        totalCount,
        progressPercent: calcPercent(completedCount, totalCount),
        isAllCompleted,
        status: getStatus(),
      } as const;
    };

    const bankCompleted = bankAccountScrapingStatus.filter((item) =>
      isCompleted(item.status),
    ).length;
    const cardCompleted = cardScrapingStatus.filter((item) => isCompleted(item.status)).length;
    const hometaxItems = [...taxInvoiceScrapingStatus, ...cashReceiptScrapingStatus];
    const hometaxCompleted = hometaxItems.filter((item) => isCompleted(item.status)).length;

    const completed = bankCompleted + cardCompleted + hometaxCompleted;
    const total =
      bankAccountScrapingStatus.length + cardScrapingStatus.length + hometaxItems.length;

    const ScrapeStatusItem: ScrapeStatusItem = {
      [SCRAPE_STATUS_ITEM_TYPE.TOTAL]: createStatusItem('전체', completed, total),
      [SCRAPE_STATUS_ITEM_TYPE.BANK]: createStatusItem(
        '은행',
        bankCompleted,
        bankAccountScrapingStatus.length,
      ),
      [SCRAPE_STATUS_ITEM_TYPE.HOMETAX]: createStatusItem(
        '홈택스',
        hometaxCompleted,
        hometaxItems.length,
      ),
      [SCRAPE_STATUS_ITEM_TYPE.CARD]: createStatusItem(
        '카드',
        cardCompleted,
        cardScrapingStatus.length,
      ),
    };

    // 홈택스는 하나의 기관으로 취급
    const HOMETAX_COUNT = 1;
    const organizationCount =
      bankAccountScrapingStatus.length + cardScrapingStatus.length + HOMETAX_COUNT;

    return {
      organizationCount,
      ScrapeStatusItem,
    };
  }, [
    bankAccountScrapingStatus,
    cardScrapingStatus,
    taxInvoiceScrapingStatus,
    cashReceiptScrapingStatus,
    overallStatus,
  ]);

  const inProgressLabels = useMemo(
    () =>
      uniq([
        ...bankAccountScrapingStatus
          .filter((item) => isInProgress(item.status))
          .map((item) => `${getBankName(item.bankCode as never) || '은행'} 수집중..`),
        ...cardScrapingStatus
          .filter((item) => isInProgress(item.status))
          .map((item) => `${getBankName(item.organizationCode as never) || '카드'} 수집중..`),
        ...taxInvoiceScrapingStatus
          .filter((item) => isInProgress(item.status))
          .map(() => '홈택스 세금계산서 수집중..'),
        ...cashReceiptScrapingStatus
          .filter((item) => isInProgress(item.status))
          .map(() => '홈택스 현금영수증 수집중..'),
      ]),
    [
      bankAccountScrapingStatus,
      cardScrapingStatus,
      taxInvoiceScrapingStatus,
      cashReceiptScrapingStatus,
    ],
  );

  const handleCloseClick = () => {
    if (ScrapeStatusItem[SCRAPE_STATUS_ITEM_TYPE.TOTAL].isAllCompleted) {
      mixpanelTrack(MIXPANEL_EVENT.SCRAPE_FLOATING_PANEL.CLOSE);
      closePanel();
      return;
    }

    overlays.open(({ isOpen: modalOpen, onClose }) => (
      <CommonModal
        width={320}
        title="데이터 수집 중입니다"
        centered
        open={modalOpen}
        onCancel={onClose}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button buttonType="ghost" buttonSize="l" onClick={onClose}>
              취소
            </Button>
            <Button
              buttonType="primary"
              buttonSize="l"
              onClick={() => {
                mixpanelTrack(MIXPANEL_EVENT.SCRAPE_FLOATING_PANEL.CLOSE);
                closePanel();
                onClose();
              }}
            >
              닫기
            </Button>
          </div>
        }
      >
        <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
          패널을 닫아도 수집은 계속 진행됩니다.
        </Text>
      </CommonModal>
    ));
  };

  const handleToggleMinimize = () => {
    if (isMinimized) {
      mixpanelTrack(MIXPANEL_EVENT.SCRAPE_FLOATING_PANEL.EXPAND);
    } else {
      mixpanelTrack(MIXPANEL_EVENT.SCRAPE_FLOATING_PANEL.MINIMIZE);
    }
    setIsMinimized((prev) => !prev);
  };

  if (!isOpen || organizationCount === 0) {
    return null;
  }

  if (isMinimized) {
    const totalProgressPercent = ScrapeStatusItem[SCRAPE_STATUS_ITEM_TYPE.TOTAL].progressPercent;
    const isAllCompleted = ScrapeStatusItem[SCRAPE_STATUS_ITEM_TYPE.TOTAL].isAllCompleted;
    return (
      <MinimizedWrapper>
        <Tooltip
          content={
            <Text textStyle="14_400" style={{ color: Colors.white }}>
              {isAllCompleted ? '데이터 수집 완료' : `데이터 수집 중 (${totalProgressPercent}%)`}
            </Text>
          }
        >
          <MinimizedButton onClick={handleToggleMinimize}>
            {isAllCompleted ? (
              <SyncLine width={24} height={24} fill={Colors.white} />
            ) : (
              <SpinnerIcon>
                <SyncLine width={24} height={24} fill={Colors.white} />
              </SpinnerIcon>
            )}
          </MinimizedButton>
        </Tooltip>
      </MinimizedWrapper>
    );
  }

  return (
    <FloatingPanelContainer>
      <ScrapeFloatingPanelHeader
        onClickClose={handleCloseClick}
        onClickToggleMinimize={handleToggleMinimize}
      />
      <ScrapeFloatingPanelList
        ScrapeStatusItem={ScrapeStatusItem}
        inProgressLabels={inProgressLabels}
      />
      <Flex
        justify="center"
        align="center"
        style={{ padding: '12px 16px', backgroundColor: Colors.gray50 }}
      >
        <SwipeText
          labels={[
            '최초 연동시 완료까지 평균 10분 가량 소요됩니다.',
            '페이지 나가셔도 수집은 계속 진행됩니다.',
          ]}
          textStyle="13_400"
          color={Colors.gray700}
          swipeInterval={5000}
          direction="horizontal"
        />
      </Flex>
    </FloatingPanelContainer>
  );
};

export default ScrapeFloatingPanel;

const isCompleted = (status: string) =>
  status === 'SUCCESS' || status === 'ERROR' || status === 'FATAL';
const isInProgress = (status: string) => status === 'IN_PROGRESS';
const calcPercent = (completed: number, total: number) =>
  total === 0 ? 0 : Math.round((completed / total) * 100);
const hasError = (status: SCRAPE_STATUS) => status === 'ERROR' || status === 'FATAL';
